if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        this.productForm = this.form?.closest('product-form');
        this.mainButton = this.form?.querySelector('[type="submit"]');

        if (!this.productForm || !this.mainButton) return;

        this.button = this.querySelector('[data-sticky-submit]');
        this.elements = {
          image: this.querySelector('[data-sticky-image]'),
          title: this.querySelector('[data-sticky-title]'),
          badge: this.querySelector('[data-sticky-badge]'),
          purchase: this.querySelector('[data-sticky-purchase]'),
          save: this.querySelectorAll('[data-sticky-save]'),
          price: this.querySelector('[data-sticky-price]'),
          compare: this.querySelector('[data-sticky-compare]'),
          buttonLabel: this.querySelector('[data-sticky-button-label]'),
          buttonPrice: this.querySelector('[data-sticky-button-price]'),
          spinner: this.querySelector('.loading__spinner'),
        };

        try {
          this.formatBadges = JSON.parse(this.dataset.formatBadges || '{}');
        } catch (error) {
          this.formatBadges = {};
        }

        this.button.addEventListener('click', this.onSubmitClick.bind(this));
        this.querySelector('[data-sticky-edit]')?.addEventListener(
          'click',
          this.onEditClick.bind(this),
        );

        // Purchase type radios live in the main form; variant radios are
        // covered by the variantChange event product-form publishes after it
        // has resolved the selected variant.
        this.form.addEventListener('change', (event) => {
          if (event.target.matches('[type="radio"]')) {
            requestAnimationFrame(() => this.update());
          }
        });

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          () => this.update(),
        );

        // Mirror the main button's loading state (set and cleared by
        // product-form.js around the add-to-cart request).
        this.loadingObserver = new MutationObserver(() => {
          this.setLoading(this.mainButton.classList.contains('loading'));
        });
        this.loadingObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['class'],
        });

        // Show the bar only once the main add-to-cart button has scrolled
        // out above the viewport, so the two CTAs are never visible together.
        // The root is extended far below the viewport so the observed state
        // only flips when the button crosses the top edge; a plain viewport
        // root misses fast scrolls that jump over the button entirely.
        this.visibilityObserver = new IntersectionObserver(
          ([entry]) => this.toggle(!entry.isIntersecting),
          { rootMargin: '0px 0px 100000px 0px', threshold: 0 },
        );
        this.visibilityObserver.observe(this.mainButton);

        // product-form resolves selectedVariant in its own connectedCallback;
        // wait for it in case this element upgrades first.
        customElements.whenDefined('product-form').then(() => this.update());
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.loadingObserver?.disconnect();
        this.visibilityObserver?.disconnect();
        this.toggle(false);
      }

      onSubmitClick() {
        if (this.button.disabled || this.button.classList.contains('loading'))
          return;

        this.form.requestSubmit();
      }

      onEditClick(event) {
        event.preventDefault();

        const target =
          this.form.querySelector('variant-selects') || this.form;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      toggle(show) {
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', String(!show));
        document.body.classList.toggle('sticky-atc-visible', show);
        document.documentElement.style.setProperty(
          '--sticky-atc-height',
          show ? `${this.offsetHeight}px` : '0px',
        );
      }

      setLoading(loading) {
        this.button.classList.toggle('loading', loading);
        this.button.setAttribute('aria-disabled', String(loading));
        this.elements.spinner?.classList.toggle('hidden', !loading);
      }

      update() {
        const variant = this.productForm.selectedVariant;

        if (!variant) return;

        const prices = this.productForm.variantPricesJson.find(
          (entry) => entry.id === variant.id,
        );
        const sellingPlanId = this.form.querySelector('[name="selling_plan"]')
          ?.value;
        const plan = sellingPlanId
          ? prices?.selling_plans?.find(
              (entry) => String(entry.id) === String(sellingPlanId),
            )
          : null;

        this.updateTitle(variant);
        this.updateImage(variant);
        this.updatePurchaseType(plan);
        this.updatePrice(prices, plan);
        this.updateAvailability(variant);
      }

      updateTitle(variant) {
        const flavorIndex = Number(this.dataset.flavorIndex);
        const formatIndex = Number(this.dataset.formatIndex);

        this.elements.title.textContent =
          flavorIndex > -1
            ? variant.options[flavorIndex]
            : this.dataset.productTitle;

        const badge =
          formatIndex > -1 ? this.formatBadges[variant.options[formatIndex]] : '';
        this.elements.badge.textContent = badge || '';
        this.elements.badge.classList.toggle('hidden', !badge);
      }

      updateImage(variant) {
        const image = this.elements.image;
        const src = variant.featured_image?.src;

        if (!image || !src) return;

        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', '144');

        if (image.dataset.currentSrc === url.href) return;

        image.dataset.currentSrc = url.href;
        image.src = url.href;
        image.removeAttribute('srcset');
        image.alt = variant.featured_image.alt || '';
      }

      updatePurchaseType(plan) {
        this.elements.purchase.textContent = plan
          ? this.dataset.textSubscription
          : this.dataset.textOneTime;
      }

      updatePrice(prices, plan) {
        if (!prices) return;

        let priceHtml = prices.price.html;
        let compareHtml = '';
        let savePercent = 0;

        if (plan) {
          priceHtml = plan.price.html;
          compareHtml = prices.price.html;
          savePercent = Math.round(
            (1 - plan.price.value / prices.price.value) * 100,
          );
        } else if (prices.compare_at_price.value > prices.price.value) {
          compareHtml = prices.compare_at_price.html;
          savePercent = Math.round(
            (1 - prices.price.value / prices.compare_at_price.value) * 100,
          );
        }

        this.elements.price.innerHTML = priceHtml;
        this.elements.compare.innerHTML = compareHtml;
        this.elements.compare.classList.toggle('hidden', !compareHtml);

        const saveText =
          savePercent > 0
            ? this.dataset.textSave.replace('[percent]', savePercent)
            : '';
        this.elements.save.forEach((element) => {
          element.textContent = saveText;
          element.classList.toggle('hidden', !saveText);
        });

        this.elements.buttonPrice.innerHTML = compareHtml
          ? `${priceHtml} <s>${compareHtml}</s>`
          : priceHtml;
      }

      updateAvailability(variant) {
        const available = Boolean(variant.available);

        this.button.disabled = !available;
        this.elements.buttonLabel.textContent = available
          ? window.variantStrings.addToCart
          : window.variantStrings.soldOut;
        this.elements.buttonPrice.classList.toggle('hidden', !available);
      }
    },
  );
}
